import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, TwoUp, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

const STATES = [
 { name: 'Pending', desc: 'Operation still running — e.g. waiting for an API response.', tone: 'border-amber-400 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20' },
 { name: 'Fulfilled', desc: 'Completed successfully — e.g. user data received.', tone: 'border-green-400 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20' },
 { name: 'Rejected', desc: 'Failed — e.g. a network error.', tone: 'border-red-400 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20' },
];

export const JSPromises: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Promises</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    An object representing the future result of an async operation — success or failure. Promises replace callback hell with chaining, centralized error handling, and real execution guarantees.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="17 min read" />
  </div>

  {/* 1. The Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>Callbacks solved one thing well: run code after an async task completes. But a real production workflow has many steps:</Para>
   <CodeBlock snippets={[{ language: 'text', code: `Create order → Validate coupon → Process payment →\nReserve inventory → Assign driver → Send notification →\nUpdate analytics → Save audit logs` }]} defaultLanguage="text" id="prom-workflow" />
   <Para>Expressed with callbacks, that becomes a deeply nested pyramid:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `createOrder(() => {\n  validateCoupon(() => {\n    processPayment(() => {\n      reserveInventory(() => {\n        assignDriver(() => {\n          sendNotification(() => {\n            updateAnalytics(() => {\n              saveAuditLog(() => {});\n            });\n          });\n        });\n      });\n    });\n  });\n});` }]} defaultLanguage="javascript" id="prom-hell" />
   <Para>Hard to read, hard to debug, hard to maintain — <Mark>callback hell</Mark>. JavaScript needed a better abstraction: the <Mark>Promise</Mark>.</Para>
  </section>

  {/* 2. Why Existing Solutions Fail */}
  <section className="mb-12">
   <SectionHeading n={2}>Why Existing Solutions Fail</SectionHeading>
   <Para>Callbacks weren&apos;t bad — they solved the async problem but introduced three new ones:</Para>
   <SubHeading>1. Callback hell</SubHeading>
   <Para>Deep nesting that drifts right and resists change.</Para>
   <SubHeading>2. Difficult error handling</SubHeading>
   <Para>With <Code>callback(error, result)</Code>, every layer must manually propagate errors.</Para>
   <SubHeading>3. Inversion of control</SubHeading>
   <Para>With <Code>paymentGateway(callback)</Code> you lose control over <em>whether</em>, <em>when</em>, and <em>how many times</em> your callback runs. If a payment callback fires twice, the customer is charged twice.</Para>
   <Callout tone="warning" title="Missing guarantees">Promises were designed to provide stronger, predictable execution guarantees.</Callout>
  </section>

  {/* 3. Definition */}
  <section className="mb-12">
   <SectionHeading n={3}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200">A <Mark>Promise</Mark> is an object that represents the eventual success or failure of an asynchronous operation.</p>
   </div>
   <Para>Think of it as a <Mark>future result</Mark>. The value may not exist yet, but JavaScript guarantees you&apos;ll eventually get either a success or a failure.</Para>
  </section>

  {/* 4. Core Idea */}
  <section className="mb-12">
   <SectionHeading n={4}>The Core Idea</SectionHeading>
   <Para>Callbacks say &quot;give me a function and I&apos;ll call it later.&quot; Promises flip it: &quot;give me an object representing future completion, and <Mark>you</Mark> decide what to do with it.&quot;</Para>
   <TwoUp
    left={{ title: 'Callback style', items: ['payment(callback)', 'You hand over control', 'No execution guarantees'] }}
    right={{ title: 'Promise style', items: ['payment().then(...).catch(...)', 'You keep control', 'Predictable, one-time resolution'] }}
   />
  </section>

  {/* 5. Internal Working: lifecycle + states */}
  <section className="mb-12">
   <SectionHeading n={5}>Internal Working</SectionHeading>
   <Para>Every promise follows a lifecycle: it always starts <Code>pending</Code>, then settles <em>once</em> into <Code>fulfilled</Code> or <Code>rejected</Code> — never both.</Para>
   <div className="my-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
    {STATES.map((s) => (
     <div key={s.name} className={`rounded-xl border-2 p-4 ${s.tone}`}>
      <div className="text-sm font-bold mb-1">{s.name}</div>
      <div className="text-[13px] opacity-80">{s.desc}</div>
     </div>
    ))}
   </div>
   <Callout tone="info" title="Why state immutability matters">
    Once a promise is fulfilled it can never become rejected later (and vice versa). That one-time transition is the execution guarantee callbacks lacked — a fulfilled payment can&apos;t accidentally fire success twice.
   </Callout>
  </section>

  {/* 6. Visual Flow */}
  <section className="mb-12">
   <SectionHeading n={6}>Visual Flow</SectionHeading>
   <CodeBlock snippets={[{ language: 'text', code: `             Pending\n            /       \\\n           /         \\\n      Fulfilled     Rejected\n          ↓             ↓\n       .then()       .catch()\n                  ↘   ↙\n               .finally()` }]} defaultLanguage="text" id="prom-visualflow" />
   <Para>Success flows to <Code>.then()</Code>, failure to <Code>.catch()</Code>, and <Code>.finally()</Code> runs either way.</Para>
  </section>

  {/* 7. Simple Example: create + resolve/reject + consume */}
  <section className="mb-12">
   <SectionHeading n={7}>Simple Example</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const orderPromise = new Promise((resolve, reject) => {\n  const success = true;\n  if (success) {\n    resolve("Order Created");\n  } else {\n    reject("Order Failed");\n  }\n});` }]} defaultLanguage="javascript" id="prom-create" />
   <TwoUp
    left={{ title: 'resolve(value)', items: ['Operation succeeded', 'Pending → Fulfilled', 'Value flows to .then()'] }}
    right={{ title: 'reject(error)', items: ['Operation failed', 'Pending → Rejected', 'Error flows to .catch()'] }}
   />
   <SubHeading>Consuming with .then() and .catch()</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `orderPromise\n  .then(result => {\n    console.log(result);\n  })\n  .catch(error => {\n    console.log(error);\n  });` }]} defaultLanguage="javascript" id="prom-consume" />
   <Output>{`Order Created`}</Output>
   <Para><Code>.then()</Code> means &quot;when it succeeds, run this&quot;; <Code>.catch()</Code> means &quot;if something goes wrong, handle it here.&quot;</Para>
  </section>

  {/* 8. Deep Example: chaining */}
  <section className="mb-12">
   <SectionHeading n={8}>Deep Example: Chaining</SectionHeading>
   <Para>The biggest benefit. Compare the flat chain to the callback pyramid:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `createOrder()\n  .then(processPayment)\n  .then(assignDriver)\n  .then(sendNotification);` }]} defaultLanguage="javascript" id="prom-chain" />
   <Para>Chaining works because <Mark>every <Code>.then()</Code> returns a new promise</Mark>. Returned values flow into the next step:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `Promise.resolve(10)\n  .then(value => value * 2)\n  .then(value => value + 5)\n  .then(console.log);` }]} defaultLanguage="javascript" id="prom-chain-values" />
   <Output>{`25`}</Output>
   <Para>And if a <Code>.then()</Code> returns a promise, the chain automatically waits for it before continuing:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `getUser()\n  .then(user => {\n    return getOrders(user.id); // returns a promise\n  })\n  .then(orders => {\n    console.log(orders);       // waits for it\n  });` }]} defaultLanguage="javascript" id="prom-chain-promise" />
  </section>

  {/* 9. Production Example */}
  <section className="mb-12">
   <SectionHeading n={9}>Production Example</SectionHeading>
   <Para>The full food-delivery flow, readable top to bottom with one shared error handler:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `createOrder()\n  .then(processPayment)\n  .then(reserveInventory)\n  .then(assignDriver)\n  .then(sendNotification)\n  .then(() => {\n    console.log("Order Completed");\n  })\n  .catch(error => {\n    console.log("Order Failed");\n  })\n  .finally(() => {\n    hideLoader();\n  });` }]} defaultLanguage="javascript" id="prom-prod" />
   <Para>Readable, maintainable, scalable — and <Code>.finally()</Code> runs regardless of success or failure, perfect for hiding a loader, closing a connection, or releasing resources.</Para>
  </section>

  {/* 10. Engineering Tradeoffs: error propagation + IoC */}
  <section className="mb-12">
   <SectionHeading n={10}>Engineering Tradeoffs</SectionHeading>
   <SubHeading>Centralized error propagation</SubHeading>
   <Para>If any step fails, the error skips the remaining <Code>.then()</Code> blocks and jumps straight to <Code>.catch()</Code>:</Para>
   <CodeBlock snippets={[{ language: 'text', code: `then() → then() → Error → (skip rest) → catch()` }]} defaultLanguage="text" id="prom-err-flow" />
   <CodeBlock snippets={[{ language: 'javascript', code: `Promise.resolve(10)\n  .then(() => {\n    throw Error("Payment Failed");\n  })\n  .catch(error => {\n    console.log(error.message);\n  });` }]} defaultLanguage="javascript" id="prom-throw" />
   <Output>{`Payment Failed`}</Output>
   <SubHeading>Solving inversion of control</SubHeading>
   <Para>A promise guarantees exactly one resolution or one rejection. You regain the execution guarantees callbacks gave away — the original motivation behind promises. The tradeoff: long chains can still grow verbose and complex chains take care to debug.</Para>
  </section>

  {/* 11. Performance Considerations */}
  <section className="mb-12">
   <SectionHeading n={11}>Performance Considerations</SectionHeading>
   <Para>A sequential <Code>.then()</Code> chain runs each step one after another. When steps are independent, run them together instead of chaining:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// Sequential — total time = sum of all\nawait a().then(b).then(c);\n\n// Parallel — total time ≈ the slowest one\nconst [ra, rb, rc] = await Promise.all([a(), b(), c()]);` }]} defaultLanguage="javascript" id="prom-perf" />
   <Callout tone="info"><Code>.then()</Code> callbacks always run asynchronously via the microtask queue — even on an already-resolved promise — so they never run synchronously inline.</Callout>
  </section>

  {/* 12. Common Bugs */}
  <section className="mb-12">
   <SectionHeading n={12}>Common Bugs</SectionHeading>
   <Callout tone="warning" title="Forgetting return">
    Not returning from <Code>.then()</Code> breaks the chain — write <Code>return getOrders()</Code>, not just <Code>getOrders()</Code>.
   </Callout>
   <Callout tone="warning" title="Nesting promises">Nesting <Code>.then()</Code> inside another <Code>.then()</Code> recreates callback hell — chain them flat instead.</Callout>
   <Callout tone="warning" title="Ignoring errors">Forgetting <Code>.catch()</Code> leaves rejections unhandled.</Callout>
   <Callout tone="warning" title="Confusing resolve with return">Inside the executor, <Code>resolve()</Code> settles the promise; a plain <Code>return</Code> does not — they are different concepts.</Callout>
  </section>

  {/* 13. Debugging Stories */}
  <section className="mb-12">
   <SectionHeading n={13}>Debugging Stories</SectionHeading>
   <Callout tone="info" title="The silent rejection">
    A request &quot;does nothing&quot; on failure. The cause is usually a missing <Code>.catch()</Code> — the promise rejected, but with no handler the error vanished. Adding a single <Code>.catch()</Code> at the end of the chain surfaces it instantly.
   </Callout>
   <Callout tone="info" title="The broken chain">
    A later <Code>.then()</Code> receives <Code>undefined</Code> even though the previous step fetched data. The fix is almost always a forgotten <Code>return</Code> — without it the value never flows to the next link.
   </Callout>
  </section>

  {/* 14. TypeScript Example */}
  <section className="mb-12">
   <SectionHeading n={14}>TypeScript Example</SectionHeading>
   <CodeBlock snippets={[{ language: 'typescript', code: `function getUser(): Promise<string> {\n  return new Promise((resolve) => {\n    resolve("Sachin");\n  });\n}\n\ngetUser().then(user => {\n  console.log(user);\n});` }]} defaultLanguage="typescript" id="prom-ts" />
   <Para><Code>Promise&lt;string&gt;</Code> tells the compiler the resolved value is a string, giving type safety, autocomplete, and compile-time validation.</Para>
  </section>

  {/* 15. Java Comparison */}
  <section className="mb-12">
   <SectionHeading n={15}>Java Comparison</SectionHeading>
   <Para>Java&apos;s <Code>CompletableFuture</Code> offers similar functionality — promises inspired many modern async abstractions:</Para>
   <CodeBlock snippets={[{ language: 'java', code: `CompletableFuture<String> future =\n  CompletableFuture.supplyAsync(() -> "User");` }]} defaultLanguage="java" id="prom-java" />
  </section>

  {/* 16. Interview Discussion */}
  <section className="mb-12">
   <SectionHeading n={16}>Interview Discussion</SectionHeading>
   <div className="flex flex-wrap gap-2 mb-5">
    {['What is a Promise?', 'Why were promises introduced?', 'Promise states?', 'resolve vs reject?', 'Why does chaining work?', 'What does then() return?', 'How does error propagation work?', 'finally() vs catch()?', 'Promise vs callback?', 'How do promises solve inversion of control?'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
   <QA q="What are the three states of a promise?">
    <Mark>Pending</Mark> (running), <Mark>fulfilled</Mark> (succeeded), and <Mark>rejected</Mark> (failed). A promise starts pending, settles only once, and is immutable afterward.
   </QA>
   <QA q="What does .then() return?">
    Always a new promise. That&apos;s what makes chaining possible — a returned value becomes the next <Code>.then()</Code>&apos;s input, and a returned promise is awaited automatically.
   </QA>
   <QA q="How do promises solve inversion of control?">
    A promise guarantees exactly one resolution or rejection. Instead of handing your callback to another function, you receive a promise object and decide how to consume it — restoring predictable execution.
   </QA>
   <QA q="What is the difference between finally() and catch()?">
    <Code>.catch()</Code> handles a rejection and receives the error. <Code>.finally()</Code> runs after settling regardless of outcome, receives no value, and is used for cleanup.
   </QA>
  </section>

  {/* 15. Mental Model */}
  <section className="mb-12">
   <SectionHeading n={17}>Mental Model</SectionHeading>
   <Para>Think of online order tracking. Right after ordering you don&apos;t have the package, but you hold a <Mark>tracking number</Mark> — a guarantee you&apos;ll eventually get either the delivery or a failure notice. That tracking number is the promise.</Para>
  </section>

  {/* 16. Interview One-Liner */}
  <section className="mb-12">
   <SectionHeading n={18}>Interview One-Liner</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4">
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     A Promise is an object representing the future completion or failure of an asynchronous operation, providing predictable execution flow, chaining, and centralized error handling.
    </p>
   </div>
  </section>

  {/* 17. Most Important Takeaway */}
  <section className="mb-4">
   <SectionHeading n={19}>Most Important Takeaway</SectionHeading>
   <Callout tone="info" title="Takeaway">
    Promises were introduced to solve the limitations of callbacks. They provide a structured way to handle async operations using states, chaining, and centralized error handling. Understanding promises deeply is critical because async/await is built directly on top of them.
   </Callout>
  </section>
 </div>
);

export default JSPromises;
